// src/components/ErrorBoundary.tsx
import React, { ReactNode } from 'react';
import posthog from 'posthog-js';
import { isStagingEnvironment, isDevelopmentEnvironment } from '../config/environment';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error, errorInfo: null }; // errorInfo will be set in componentDidCatch
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error("Error caught by boundary:", error, errorInfo);
    this.setState({ errorInfo });

    if (posthog.__loaded) { // Check if PostHog is initialized
      posthog.capture('react_error_boundary_caught', {
          error_message: error.message,
          error_stack: error.stack,
          component_stack: errorInfo.componentStack,
          page_path: window.location.pathname,
      });
  }
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div className="error-page p-4 text-center bg-red-100 text-red-700 min-h-screen flex flex-col justify-center items-center">
          <h1 className="text-2xl font-bold mb-2">Oops! Something went wrong.</h1>
          <p className="mb-4">We've been notified about this issue. Please try refreshing the page or contact support if the problem persists.</p>
          {this.state.error && (isDevelopmentEnvironment() || isStagingEnvironment()) && (
            <details className="text-left bg-white p-3 rounded shadow text-sm text-gray-700 max-w-xl">
              <summary className="cursor-pointer font-medium">Error Details</summary>
              <pre className="mt-2 whitespace-pre-wrap break-all">
                <strong>Error:</strong> {this.state.error.toString()}
                {this.state.errorInfo && (
                    <>
                        <br />
                        <strong>Component Stack:</strong>
                        {this.state.errorInfo.componentStack}
                    </>
                )}
              </pre>
            </details>
          )}
           <button
            onClick={() => window.location.reload()}
            className="mt-6 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
          >
            Refresh Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;